/**
 * RunningHub - Main page for the Running Community feature
 * Follows SocialHub.tsx pattern with view switching
 */

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useRunningStore } from '@/stores/useRunningStore';
import { cn } from '@/utils/cn';
import { ActivityFeed } from '../components/ActivityFeed';
import { ActivityDetail } from '../components/ActivityDetail';
import { RecordRun } from '../components/RecordRun';
import { RunnerProfile } from '../components/RunnerProfile';
import { ClubList } from '../components/ClubList';
import { ChallengeList } from '../components/ChallengeList';
import { BestRunningTimes } from '../components/BestRunningTimes';
import type { RunningActivity } from '@/types/running';

type ViewType = 'feed' | 'explore' | 'clubs' | 'challenges' | 'profile';

interface RunningHubProps {
  isOpen: boolean;
  onClose: () => void;
  initialView?: ViewType;
}

export function RunningHub({ isOpen, onClose, initialView }: RunningHubProps) {
  const [currentView, setCurrentView] = useState<ViewType>(initialView || 'feed');
  const [showRecordRun, setShowRecordRun] = useState(false);
  const [viewingUserId, setViewingUserId] = useState<string | null>(null);
  const [selectedActivity, setSelectedActivity] = useState<RunningActivity | null>(null);

  const activityFeed = useRunningStore((state) => state.activityFeed);
  const weeklyStats = useRunningStore((state) => state.weeklyStats);
  const fetchWeeklyStats = useRunningStore((state) => state.fetchWeeklyStats);

  const handleActivityClick = (activityId: string) => {
    const found = activityFeed.find((a) => a.id === activityId);
    if (found) setSelectedActivity(found);
  };

  useEffect(() => {
    if (isOpen) {
      fetchWeeklyStats();
      if (initialView) setCurrentView(initialView);
    }
  }, [isOpen, fetchWeeklyStats, initialView]);

  const handleUserClick = (userId: string) => {
    setViewingUserId(userId);
    setCurrentView('profile');
  };

  const handleBack = () => {
    if (viewingUserId) {
      setViewingUserId(null);
      setCurrentView('feed');
    }
  };

  const formatPace = (seconds: number): string => {
    if (!seconds || seconds === Infinity) return '--:--';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const navItems: { id: ViewType; label: string }[] = [
    { id: 'feed', label: 'Feed' },
    { id: 'explore', label: 'Explore' },
    { id: 'clubs', label: 'Clubs' },
    { id: 'challenges', label: 'Challenges' },
    { id: 'profile', label: 'Profile' },
  ];

  // Clean SVG icons for navigation
  const NavIcon = ({ id, active }: { id: ViewType; active: boolean }) => {
    const color = active ? 'currentColor' : 'currentColor';
    const icons: Record<ViewType, React.ReactNode> = {
      feed: (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="3" width="7" height="9" rx="1" />
          <rect x="14" y="3" width="7" height="5" rx="1" />
          <rect x="14" y="12" width="7" height="9" rx="1" />
          <rect x="3" y="16" width="7" height="5" rx="1" />
        </svg>
      ),
      explore: (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="9" />
          <polygon points="12,7 14.5,11 12,15 9.5,11" fill={active ? color : 'none'} />
        </svg>
      ),
      clubs: (
        <svg width="22" height="22" viewBox="0 0 24 24" fill={color}>
          {/* Left person (back) */}
          <circle cx="6" cy="5" r="2.5" />
          <path d="M2 21 Q2 13 6 13 Q10 13 10 21" />
          {/* Right person (back) */}
          <circle cx="18" cy="5" r="2.5" />
          <path d="M14 21 Q14 13 18 13 Q22 13 22 21" />
          {/* Center person (front) */}
          <circle cx="12" cy="6" r="3" />
          <path d="M6.5 22 Q6.5 12 12 12 Q17.5 12 17.5 22" />
        </svg>
      ),
      challenges: (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6" />
          <path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18" />
          <path d="M4 22h16" />
          <path d="M10 22V8a2 2 0 0 1 4 0v14" />
          <path d="M8 8h8" />
        </svg>
      ),
      profile: (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="8" r="4" />
          <path d="M5 21v-2a7 7 0 0 1 14 0v2" />
        </svg>
      ),
    };
    return icons[id];
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 bg-[#0a0f14]"
        >
          {/* Header */}
          <div className="sticky top-0 z-10 bg-[#0a0f14] border-b border-glass-border">
            <div className="flex items-center justify-between p-4">
              <div className="flex items-center gap-3">
                {viewingUserId ? (
                  <button
                    onClick={handleBack}
                    className="w-8 h-8 rounded-full bg-slate-800 flex items-center justify-center text-white"
                  >
                    ←
                  </button>
                ) : (
                  <button
                    onClick={onClose}
                    className="w-8 h-8 rounded-full bg-slate-800 flex items-center justify-center text-white"
                  >
                    ✕
                  </button>
                )}
                <div>
                  <h1 className="text-xl font-bold text-white flex items-center gap-2">
                    <svg width="20" height="20" viewBox="0 0 64 64" fill="none" className="text-emerald-400">
                      {/* Motion lines */}
                      <rect x="0" y="20" width="15" height="4" rx="2" fill="#06b6d4" />
                      <rect x="3" y="28" width="13" height="4" rx="2" fill="#06b6d4" />
                      <rect x="0" y="36" width="15" height="4" rx="2" fill="#06b6d4" />
                      {/* Head */}
                      <circle cx="46" cy="16" r="6" fill="#06b6d4" />
                      {/* Torso */}
                      <path d="M44 22L38 38" stroke="#06b6d4" strokeWidth="6" strokeLinecap="round" />
                      {/* Arms */}
                      <path d="M42 26L50 30L56 24" stroke="#06b6d4" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />
                      <path d="M40 28L32 24L24 28" stroke="#06b6d4" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />
                      {/* Legs */}
                      <path d="M38 38L24 48L14 56" stroke="#06b6d4" strokeWidth="5" strokeLinecap="round" strokeLinejoin="round" />
                      <path d="M38 38L52 48L46 60" stroke="#06b6d4" strokeWidth="5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    Running
                  </h1>
                  <p className="text-xs text-text-secondary">Track runs with air quality</p>
                </div>
              </div>

              {/* Quick Stats */}
              {weeklyStats && currentView === 'feed' && (
                <div className="hidden sm:flex items-center gap-4 text-xs">
                  <div className="text-center">
                    <div className="text-white font-bold">{weeklyStats.totalRuns}</div>
                    <div className="text-text-secondary">runs</div>
                  </div>
                  <div className="text-center">
                    <div className="text-white font-bold">{weeklyStats.totalDistance.toFixed(1)}km</div>
                    <div className="text-text-secondary">this week</div>
                  </div>
                  <div className="text-center">
                    <div className="text-emerald-400 font-bold">AQI {Math.round(weeklyStats.averageAQI)}</div>
                    <div className="text-text-secondary">avg exposure</div>
                  </div>
                </div>
              )}
            </div>

            {/* Weekly Summary Card (mobile) */}
            {weeklyStats && currentView === 'feed' && (
              <div className="sm:hidden px-4 pb-3">
                <div className="bg-gradient-to-r from-emerald-500/20 to-cyan-500/20 rounded-xl p-3 border border-emerald-500/20">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="text-center">
                        <div className="text-lg font-bold text-white">{weeklyStats.totalRuns}</div>
                        <div className="text-[10px] text-text-secondary">runs</div>
                      </div>
                      <div className="text-center">
                        <div className="text-lg font-bold text-white">{weeklyStats.totalDistance.toFixed(1)}</div>
                        <div className="text-[10px] text-text-secondary">km</div>
                      </div>
                      <div className="text-center">
                        <div className="text-lg font-bold text-white">{formatPace(weeklyStats.fastestPace)}</div>
                        <div className="text-[10px] text-text-secondary">pace</div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-emerald-400 font-bold text-lg">AQI {Math.round(weeklyStats.averageAQI)}</div>
                      <div className="text-[10px] text-text-secondary">avg exposure</div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Content */}
          <div className="h-[calc(100vh-140px)] overflow-y-auto pb-20 bg-[#0a0f14]">
            <AnimatePresence mode="wait">
              {currentView === 'feed' && (
                <motion.div
                  key="feed"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                >
                  {/* Best Running Times Widget */}
                  <div className="p-4">
                    <BestRunningTimes />
                  </div>

                  {/* Activity Feed */}
                  <ActivityFeed
                    type="all"
                    onUserClick={handleUserClick}
                    onActivityClick={handleActivityClick}
                  />
                </motion.div>
              )}

              {currentView === 'explore' && (
                <motion.div
                  key="explore"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                  className="p-4"
                >
                  <div className="text-center py-12">
                    <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-emerald-500/20 flex items-center justify-center">
                      <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-emerald-400">
                        <circle cx="12" cy="12" r="9" />
                        <polygon points="12,7 14.5,11 12,15 9.5,11" fill="currentColor" />
                      </svg>
                    </div>
                    <h3 className="text-xl font-bold text-white mb-2">Explore Runners</h3>
                    <p className="text-text-secondary">
                      Discover popular routes and runners in your area
                    </p>
                  </div>
                  <ActivityFeed
                    type="nearby"
                    onUserClick={handleUserClick}
                    onActivityClick={handleActivityClick}
                  />
                </motion.div>
              )}

              {currentView === 'clubs' && (
                <motion.div
                  key="clubs"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                >
                  <ClubList />
                </motion.div>
              )}

              {currentView === 'challenges' && (
                <motion.div
                  key="challenges"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                >
                  <ChallengeList />
                </motion.div>
              )}

              {currentView === 'profile' && (
                <motion.div
                  key="profile"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                >
                  <RunnerProfile
                    userId={viewingUserId || undefined}
                    onUserClick={handleUserClick}
                  />
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Record Run FAB */}
          <motion.button
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            onClick={() => setShowRecordRun(true)}
            className={cn(
              'fixed bottom-24 right-4 z-20',
              'w-14 h-14 rounded-full',
              'bg-gradient-to-r from-emerald-500 to-cyan-500',
              'flex items-center justify-center',
              'shadow-lg shadow-emerald-500/30'
            )}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
          </motion.button>

          {/* Bottom Navigation */}
          <div className="fixed bottom-0 left-0 right-0 bg-[#0a0f14] border-t border-glass-border safe-area-bottom">
            <div className="flex items-center justify-around py-3 px-2">
              {navItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => {
                    setViewingUserId(null);
                    setCurrentView(item.id);
                  }}
                  className={cn(
                    'relative flex flex-col items-center gap-1.5 px-3 py-1.5 rounded-xl transition-all min-w-[56px]',
                    currentView === item.id
                      ? 'text-emerald-400'
                      : 'text-text-secondary hover:text-white'
                  )}
                >
                  <NavIcon id={item.id} active={currentView === item.id} />
                  <span className="text-[10px] font-medium">{item.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Record Run Modal */}
          <RecordRun
            isOpen={showRecordRun}
            onClose={() => setShowRecordRun(false)}
            onSuccess={() => {
              setShowRecordRun(false);
              setCurrentView('feed');
            }}
          />

          {/* Activity Detail / Re-share */}
          <ActivityDetail
            activity={selectedActivity}
            isOpen={!!selectedActivity}
            onClose={() => setSelectedActivity(null)}
          />
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export default RunningHub;
